/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { useState, useEffect } from "react";
import { getContactDetails, getContactAttributes } from "../services/Contacts";
import { v2 } from "@prftesp/cle-logger";
import { redactPII } from "../../../utility/helpers";
import { PII_FIELDS } from "../../../utility/constants";

const COMPONENT_NAME = "admin ccp hooks/useCurrentContact";

// globally available current contact id, should only be used for logging
window.paceCurrentContactId = undefined;

// globally available agent instance
let currentAgent = null;

const useCurrentContact = (onContactEnd) => {
	const [currentContact, setCurrentContact] = useState(null);
	// We only use contacts inside of its own setter function
	// so we need to disable the unused warning as it is passed into the setter function.
	// eslint-disable-next-line
	const [contacts, setContacts] = useState({});
	const updateCurrentContact = (contact) => {
		window.paceCurrentContactId = contact && contact.contactId ? contact.contactId : undefined;
		setCurrentContact(contact);

		v2.verbose("Updating current contact", {
			componentName: COMPONENT_NAME,
			additionalData: {
				data: {
					contact: redactPII(contact, PII_FIELDS.contact),
				},
			},
		});
	};

	useEffect(() => {
		const initializeContact = (contact) => {
			setContacts((oldContacts) => {
				let newContacts = oldContacts;
				if (contact && !oldContacts[contact.contactId]) {
					newContacts = { ...oldContacts, [contact.contactId]: true };
					contact.onAccepted(() => {
						try {
							// reset contact state after it has been accepted to push
							// any new changes to embedded iframes
							const contactDetails = getContactDetailsObject(contact);

							updateCurrentContact(contactDetails);
						} catch (error) {
							console.log(error);
						}
					});
					contact.onDestroy((contact) => {
						onContactEnd(contact.getContactId());
						setContacts((oldContacts) => {
							const newContacts = { ...oldContacts };
							delete newContacts[contact.contactId];
							return newContacts;
						});
						//Checking if there are more contacts for the agent than the one being destroyed.
						//If not then clear the CCP window. If there is, onViewContact will take care of changing the attributes.
						if (currentAgent && currentAgent.getContacts().length === 0) {
							updateCurrentContact(null);
						}
					});
				}
				return newContacts;
			});
		};
		const events = window.connect.core.getEventBus();
		const viewContactSubscription = events.subscribe(window.connect.ContactEvents.VIEW, (event) => {
			if (currentAgent && event.contactId) {
				const contact = currentAgent.getContacts().find((contact) => contact.contactId === event.contactId);
				if (contact) {
					initializeContact(contact);
					const contactDetails = getContactDetailsObject(contact);
					updateCurrentContact(contactDetails);
				}
			}
		});

		const contactSubscription = window.connect.contact((contact) => {
			// trigger view contact to initialize pace tabs only if there are no active contacts
			if (!window.paceCurrentContactId) {
				window.connect.core.viewContact(contact.contactId);
			}
			initializeContact(contact);
		});

		const agentSubscription = window.connect.agent(async (agent) => {
			currentAgent = agent;
		});
		return () => {
			contactSubscription.unsubscribe();
			agentSubscription.unsubscribe();
			viewContactSubscription.unsubscribe();
			currentAgent = null;
		};
	}, [onContactEnd]);
	return currentContact;
};

const getContactDetailsObject = (contact) => {
	return {
		...getContactDetails(contact),
		attributes: getContactAttributes(contact),
	};
};

export default useCurrentContact;
